// Компонент для отображения агрегатов в footer таблицы
import React from 'react';
import { Table } from 'antd';
import type { ColumnConfig, Row, AggregateType } from './types';

interface TableSummaryProps {
  columns: ColumnConfig[];
  data: Row[];
}

/**
 * Вычисляет агрегат для колонки
 */
function calculateAggregate(
  data: Row[],
  field: string,
  aggregateType: AggregateType
): number | string {
  const values = data
    .map((row) => row[field])
    .filter((val) => val !== null && val !== undefined)
    .map((val) => (typeof val === 'number' ? val : parseFloat(val)))
    .filter((val) => !isNaN(val));

  if (values.length === 0) return '—';

  switch (aggregateType) {
    case 'sum':
      return values.reduce((acc, val) => acc + val, 0);

    case 'avg':
      return values.reduce((acc, val) => acc + val, 0) / values.length;

    case 'count':
      return values.length;

    case 'min':
      return Math.min(...values);

    case 'max':
      return Math.max(...values);

    default:
      return '—';
  }
}

/**
 * Форматирует значение агрегата
 */
function formatAggregate(value: number | string, aggregateType: AggregateType): string {
  if (typeof value === 'string') return value;

  if (aggregateType === 'count') {
    return value.toString();
  }

  if (aggregateType === 'avg') {
    return value.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  }

  return value.toLocaleString('ru-RU');
}

export const TableSummary: React.FC<TableSummaryProps> = ({ columns, data }) => {
  // Проверяем, есть ли колонки с агрегатами
  const hasAggregates = columns.some((col) => col.aggregate);

  if (!hasAggregates || data.length === 0) {
    return null;
  }

  return (
    <Table.Summary fixed>
      <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
        {columns.map((col, index) => {
          if (index === 0) {
            // Первая колонка - текст "ИТОГО"
            return (
              <Table.Summary.Cell key={col.field} index={index}>
                ИТОГО:
              </Table.Summary.Cell>
            );
          }

          if (!col.aggregate) {
            // Колонка без агрегата
            return <Table.Summary.Cell key={col.field} index={index} />;
          }

          // Вычисляем и отображаем агрегат
          const value = calculateAggregate(data, col.field, col.aggregate);
          const formatted = formatAggregate(value, col.aggregate);

          return (
            <Table.Summary.Cell key={col.field} index={index}>
              {formatted}
            </Table.Summary.Cell>
          );
        })}
      </Table.Summary.Row>
    </Table.Summary>
  );
};
